/**
 * Structural grid keyboard navigation using `role="grid"`, `role="row"`, and `role="gridcell"`.
 * Logical columns are derived from **DOM order** of direct child gridcells, plus optional
 * `aria-colspan` on merged cells. Optional `aria-colindex` overrides the auto-incremented start
 * when needed (not used in resourcing).
 */

import {
  getFocusableSelector,
  isSequentialTabStop
} from 'v5/platform/dom/isFocusable';

/** Same selector as {@link getFocusableSelector} (platform-wide focusable definition). */
const FOCUSABLE_SELECTOR = getFocusableSelector();

export type GridNavDirection = 'up' | 'down' | 'left' | 'right';

export interface RowCellInfo {
  el: HTMLElement;
  /** 1-based */
  start: number;
  span: number;
}

function parseColindex(el: HTMLElement): number {
  const v = el.getAttribute('aria-colindex');
  if (!v) {
    return -1;
  }
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : -1;
}

function parseColspan(el: HTMLElement): number {
  const v = el.getAttribute('aria-colspan');
  if (!v) {
    return 1;
  }
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/**
 * Direct child gridcells of a row, sorted by starting column. Cells without `aria-colindex` get
 * the next logical column after the previous cell’s range (DOM order).
 */
export function getRowCellInfos(row: HTMLElement): RowCellInfo[] {
  const cells = Array.from(
    row.querySelectorAll(':scope > [role="gridcell"]')
  ).filter((c): c is HTMLElement => c instanceof HTMLElement);

  const infos: RowCellInfo[] = [];
  let fallback = 1;
  for (const el of cells) {
    let start = parseColindex(el);
    if (start < 1) {
      start = fallback;
    }
    const span = parseColspan(el);
    infos.push({ el, start, span });
    fallback = start + span;
  }
  infos.sort((a, b) => a.start - b.start);
  return infos;
}

/** All `role="row"` descendants of the grid, in document order. */
export function getGridRows(grid: HTMLElement): HTMLElement[] {
  return Array.from(grid.querySelectorAll('[role="row"]')).filter(
    (el): el is HTMLElement => el instanceof HTMLElement
  );
}

export function findContainingGridCell(
  root: HTMLElement,
  start: HTMLElement | null
): HTMLElement | null {
  let n: HTMLElement | null = start;
  while (n && root.contains(n)) {
    if (n.getAttribute('role') === 'gridcell') {
      return n;
    }
    n = n.parentElement;
  }
  return null;
}

export function findCellCoveringColumn(
  row: HTMLElement,
  logicalCol: number
): HTMLElement | null {
  const infos = getRowCellInfos(row);
  if (infos.length === 0) {
    return null;
  }
  for (const info of infos) {
    if (logicalCol >= info.start && logicalCol < info.start + info.span) {
      return info.el;
    }
  }
  let best: RowCellInfo | null = null;
  for (const info of infos) {
    if (info.start <= logicalCol) {
      if (!best || info.start > best.start) {
        best = info;
      }
    }
  }
  return best?.el ?? infos[0]!.el;
}

/**
 * True when an arrow in `direction` should leave the grid (no structural neighbor) and move
 * sequential focus outside the grid: down/up on first/last row; right/left on bottom-right /
 * top-left corners (mirrors “last col + last row” / “first col + first row”).
 */
export function isGridEscapeEdge(
  grid: HTMLElement,
  currentCell: HTMLElement,
  direction: GridNavDirection
): boolean {
  const rowEl = currentCell.closest('[role="row"]');
  if (!rowEl || !grid.contains(rowEl)) {
    return false;
  }

  const rows = getGridRows(grid);
  const rowIndex = rows.indexOf(rowEl as HTMLElement);
  if (rowIndex < 0) {
    return false;
  }

  const infos = getRowCellInfos(rowEl as HTMLElement);
  const idx = infos.findIndex(i => i.el === currentCell);
  if (idx < 0) {
    return false;
  }

  const lastRow = rowIndex >= rows.length - 1;
  const firstRow = rowIndex <= 0;
  const lastCol = idx >= infos.length - 1;
  const firstCol = idx <= 0;

  switch (direction) {
    case 'down':
      return lastRow;
    case 'up':
      return firstRow;
    case 'right':
      return lastRow && lastCol;
    case 'left':
      return firstRow && firstCol;
    default:
      return false;
  }
}

function deepestLastElement(root: HTMLElement): HTMLElement {
  let n: HTMLElement | null = root;
  while (n?.lastElementChild) {
    n = n.lastElementChild as HTMLElement;
  }
  return n ?? root;
}

/** Nearest `Document` root that contains `node` for a depth-first element walk (`body`, else `documentElement`). */
function getElementTreeRootForWalk(node: HTMLElement): HTMLElement | null {
  const doc = node.ownerDocument;
  if (!doc) {
    return null;
  }
  if (doc.body?.contains(node)) {
    return doc.body;
  }
  if (doc.documentElement?.contains(node)) {
    return doc.documentElement;
  }
  return null;
}

/**
 * First sequential tab stop after `container` in document order (tabIndex ≥ 0, not disabled).
 * Ignores `tabindex="-1"` cells so it still works when the grid uses roving focus on gridcells.
 * Walks forward with a `TreeWalker` from the last node inside `container` instead of listing every
 * focusable element in the document.
 */
export function findNextFocusableAfterContainer(
  container: HTMLElement
): HTMLElement | null {
  const root = getElementTreeRootForWalk(container);
  const doc = container.ownerDocument;
  if (!root || !doc) {
    return null;
  }
  const last = deepestLastElement(container);
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  walker.currentNode = last;
  let n: Node | null = walker.nextNode();
  while (n) {
    if (
      n instanceof HTMLElement &&
      !container.contains(n) &&
      isSequentialTabStop(n)
    ) {
      return n;
    }
    n = walker.nextNode();
  }
  return null;
}

/**
 * Last sequential tab stop before `container` in document order (tabIndex ≥ 0, not disabled).
 * Walks backward with a `TreeWalker` from `container` until a tab stop is found (same result as
 * scanning all preceding focusables, without building that list).
 */
export function findPreviousFocusableBeforeContainer(
  container: HTMLElement
): HTMLElement | null {
  const root = getElementTreeRootForWalk(container);
  const doc = container.ownerDocument;
  if (!root || !doc) {
    return null;
  }
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  walker.currentNode = container;
  let n: Node | null = walker.previousNode();
  while (n) {
    if (
      n instanceof HTMLElement &&
      !container.contains(n) &&
      isSequentialTabStop(n)
    ) {
      return n;
    }
    n = walker.previousNode();
  }
  return null;
}

export function findStructuralNeighbor(
  grid: HTMLElement,
  currentCell: HTMLElement,
  direction: GridNavDirection
): HTMLElement | null {
  const rowEl = currentCell.closest('[role="row"]');
  if (!rowEl || !grid.contains(rowEl)) {
    return null;
  }

  const rows = getGridRows(grid);
  const rowIndex = rows.indexOf(rowEl as HTMLElement);
  if (rowIndex < 0) {
    return null;
  }

  const infos = getRowCellInfos(rowEl as HTMLElement);
  const idx = infos.findIndex(i => i.el === currentCell);
  if (idx < 0) {
    return null;
  }

  switch (direction) {
    case 'left':
      return idx > 0 ? infos[idx - 1]!.el : null;
    case 'right':
      return idx < infos.length - 1 ? infos[idx + 1]!.el : null;
    case 'down': {
      if (rowIndex >= rows.length - 1) {
        return null;
      }
      const anchor = infos[idx]!.start;
      return findCellCoveringColumn(rows[rowIndex + 1]!, anchor);
    }
    case 'up': {
      if (rowIndex <= 0) {
        return null;
      }
      const anchor = infos[idx]!.start;
      return findCellCoveringColumn(rows[rowIndex - 1]!, anchor);
    }
    default:
      return null;
  }
}

export function getFirstFocusableIn(
  container: HTMLElement
): HTMLElement | null {
  const list = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  for (const el of list) {
    if (isSequentialTabStop(el)) {
      return el;
    }
  }
  return null;
}

/**
 * Shortest distance from a viewport point to a rectangle (0 if the point lies inside).
 */
export function distancePointToRect(
  px: number,
  py: number,
  rect: DOMRectReadOnly
): number {
  const cx = Math.min(Math.max(px, rect.left), rect.right);
  const cy = Math.min(Math.max(py, rect.top), rect.bottom);
  const dx = px - cx;
  const dy = py - cy;
  return Math.hypot(dx, dy);
}

/**
 * True if `start` is `cell` or a descendant of `cell`, and some strict descendant of `cell` on the
 * ancestor chain from `start` is a tabbable control (so the browser would focus it on click, not the
 * roving gridcell).
 */
export function isPointerEventOnInnerTabbable(
  cell: HTMLElement,
  start: HTMLElement
): boolean {
  let n: HTMLElement | null = start;
  while (n && n !== cell) {
    if (isSequentialTabStop(n)) {
      return true;
    }
    n = n.parentElement;
  }
  return false;
}

/**
 * Among tabbable descendants of `cell`, returns the one whose bounding box is closest to
 * `(clientX, clientY)` in viewport space. Ties break by document order (earlier node wins).
 */
export function findClosestTabbableDescendantInCell(
  cell: HTMLElement,
  clientX: number,
  clientY: number
): HTMLElement | null {
  const list = cell.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  let best: HTMLElement | null = null;
  let bestD = Number.POSITIVE_INFINITY;
  for (const el of list) {
    if (!isSequentialTabStop(el)) {
      continue;
    }
    const rect = el.getBoundingClientRect();
    const d = distancePointToRect(clientX, clientY, rect);
    if (d < bestD) {
      best = el;
      bestD = d;
      continue;
    }
    if (best && d === bestD) {
      const pos = el.compareDocumentPosition(best);
      // FOLLOWING: `best` comes after `el` → prefer earlier `el`
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
        best = el;
      }
    }
  }
  return best;
}

/**
 * Focus first tabbable descendant, else the cell itself (must be focusable, e.g. tabIndex={-1}).
 */
export function focusGridNavCell(cell: HTMLElement): void {
  const inner = getFirstFocusableIn(cell);
  if (inner) {
    inner.focus();
    return;
  }
  cell.focus();
}

export function shouldDelegateTypingToField(active: Element | null): boolean {
  if (!(active instanceof HTMLElement)) {
    return false;
  }
  const tag = active.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    return true;
  }
  if (active.isContentEditable) {
    return true;
  }
  return Boolean(active.closest('[role="textbox"], [role="combobox"]'));
}

export function directionFromKey(key: string): GridNavDirection | null {
  switch (key) {
    case 'ArrowDown':
      return 'down';
    case 'ArrowUp':
      return 'up';
    case 'ArrowRight':
      return 'right';
    case 'ArrowLeft':
      return 'left';
    default:
      return null;
  }
}

/** APG-style jumps: row vs whole grid (Ctrl/Cmd modify Home/End). */
export type GridJumpAction = 'rowStart' | 'rowEnd' | 'gridStart' | 'gridEnd';

/**
 * Maps Home/End with optional Ctrl or Meta (Cmd) to a jump action.
 * Ctrl/Meta+Home/End must be checked before plain Home/End.
 */
export function jumpActionFromKeyboardEvent(
  event: KeyboardEvent
): GridJumpAction | null {
  if (event.key !== 'Home' && event.key !== 'End') {
    return null;
  }
  const mod = event.ctrlKey || event.metaKey;
  if (event.key === 'Home') {
    return mod ? 'gridStart' : 'rowStart';
  }
  return mod ? 'gridEnd' : 'rowEnd';
}

/**
 * Target cell for a jump from the current cell (same row vs first/last row of the grid).
 */
export function findJumpTarget(
  grid: HTMLElement,
  currentCell: HTMLElement,
  action: GridJumpAction
): HTMLElement | null {
  const rowEl = currentCell.closest('[role="row"]');
  if (!rowEl || !grid.contains(rowEl)) {
    return null;
  }

  const rows = getGridRows(grid);
  if (rows.length === 0) {
    return null;
  }

  switch (action) {
    case 'rowStart': {
      const infos = getRowCellInfos(rowEl as HTMLElement);
      return infos[0]?.el ?? null;
    }
    case 'rowEnd': {
      const infos = getRowCellInfos(rowEl as HTMLElement);
      return infos.length > 0 ? infos[infos.length - 1]!.el : null;
    }
    case 'gridStart': {
      const infos = getRowCellInfos(rows[0]!);
      return infos[0]?.el ?? null;
    }
    case 'gridEnd': {
      const infos = getRowCellInfos(rows[rows.length - 1]!);
      return infos.length > 0 ? infos[infos.length - 1]!.el : null;
    }
  }
}
